/**
 * TableView: Zeigt Karten in einer Tabellenansicht an
 * - Spalten: Name, SuperTypes, Types, SubTypes, CMC, ManaCost, Quantity, OwnedQuantity
 * - Kategorien klappbar (wie in anderen Views)
 * - Hover auf Zeile zeigt Kartenvorschau
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ManaSymbols from '../../../ui/ManaSymbols';
import CategoryHeader from '../../../ui/CategoryHeader';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { formatPrice } from '../utils/formatPrice';
import { useDeckCardActions } from '../hooks/useDeckCardActions';
import { useDeckViewState } from '../hooks/useDeckViewState';
import { useShouldBeTransparent } from '../hooks/useShouldBeTransparent';
import { useDeckDragHandlers } from '../hooks/useDeckDragHandlers';
import { useDelayedHover } from '../hooks/useDelayedHover';
import CardPreview from '../components/CardPreview.js';
import CardContextMenu from '../components/CardContextMenu.js';
import LabelIndicators from '../components/LabelIndicators.js';
import '../../../components/CardViews.css';
import '../../../ui/dataTable.css';

// ========== KONFIGURATIONSKONSTANTEN ==========
const HOVER_DELAY_MS = 200;            // Verzögerung für Hover Start/Ende

// ========== TABELLEN-ZEILE ==========
function TableRow({
  card,
  canEditQuantity,
  onQuantityDelta,
  isOwnProfile,
  onSetAsCommander,
  onShowDetails,
  onSwitchPrint,
  onPreviewStart,
  onPreviewEnd,
  hoverDelayMs,
  activePreviewCardId,
  highlightedNewCardId,
  deckLabels,
  cardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { card },
  });

  const {
    isHovered: isPreviewHovered,
    onMouseEnter: onPreviewHoverEnter,
    onMouseLeave: onPreviewHoverLeave,
    setHoveredImmediate: setPreviewHoveredImmediate,
  } = useDelayedHover({
    enterDelay: hoverDelayMs,
    leaveDelay: hoverDelayMs,
  });

  const rowRef = useRef(null);
  const previewPositionRef = useRef(null);
  
  const handleSetCommander = useCallback(() => {
    if (onSetAsCommander) {
      onSetAsCommander(card.id, true);
    }
  }, [card.id, onSetAsCommander]);
  
  const handlePreviewCellEnter = useCallback((e) => {
    previewPositionRef.current = { x: e.clientX, y: e.clientY };
    onPreviewHoverEnter();
  }, [onPreviewHoverEnter]);

  const handlePreviewCellLeave = useCallback(() => {
    onPreviewHoverLeave();
  }, [onPreviewHoverLeave]);

  const handlePreviewCellMove = useCallback((e) => {
    previewPositionRef.current = { x: e.clientX, y: e.clientY };
    if (isPreviewHovered) {
      onPreviewStart(card, { x: e.clientX, y: e.clientY });
    }
  }, [isPreviewHovered, card, onPreviewStart]);

  useEffect(() => {
    if (isPreviewHovered) {
      if (onPreviewStart && previewPositionRef.current) {
        onPreviewStart(card, previewPositionRef.current);
      }
      return;
    }

    if (onPreviewEnd && activePreviewCardId === card.id) {
      onPreviewEnd();
    }
  }, [card, isPreviewHovered, onPreviewEnd, onPreviewStart, activePreviewCardId]);

  useEffect(() => {
    if (!isDragging) return;
    setPreviewHoveredImmediate(false);
    if (onPreviewEnd) {
      onPreviewEnd();
    }
  }, [isDragging, onPreviewEnd, setPreviewHoveredImmediate]);
  
  // Parse types
  const superTypes = card.superTypes?.join(', ') || '';
  const types = card.types?.join(', ') || '';
  const subTypes = card.subTypes?.join(', ') || '';
  const ownedQty = card.totalOwnedQuantity ?? card.ownedQuantity ?? '-';
  
  const setRefs = useCallback((el) => {
    setNodeRef(el);
    rowRef.current = el;
  }, [setNodeRef]);
  
  return (
    <tr
      ref={setRefs}
      {...listeners}
      {...attributes}
      data-card-id={card.id || card.scryfallId}
      className={(card.id || card.scryfallId) === highlightedNewCardId ? 'card-just-added' : ''}
      style={{
        opacity: isDragging ? 0 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseMove={handlePreviewCellMove}
    >
      {/* Name */}
      <td className="data-table__cell--primary"
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        <span className="table-cell__text">{card.name}</span>
        {card.isInCollection === false && (
          <span className="table-cell__missing-badge">Fehlt</span>
        )}
      </td>

      {/* Labels */}
      <td>
        <LabelIndicators labels={cardLabels} variant="dots" />
      </td>

      {/* SuperTypes */}
      <td
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        {superTypes}
      </td>

      {/* Types */}
      <td
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        {types}
      </td>

      {/* SubTypes */}
      <td
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        {subTypes}
      </td>

      {/* CMC */}
      <td className="data-table__cell--num"
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        {card.cmc ?? '-'}
      </td>

      {/* ManaCost */}
      <td className="data-table__cell--num"
        onMouseEnter={handlePreviewCellEnter}
        onMouseLeave={handlePreviewCellLeave}
      >
        {card.manaCost && <ManaSymbols manaCost={card.manaCost} size={14} />}
      </td>

      {/* Quantity */}
      <td className="data-table__cell--num">
        <div className="table-cell__quantity-wrapper">
          {canEditQuantity && (
            <button
              type="button"
              className="table-qty-btn"
              onClick={(e) => {
                e.stopPropagation();
                const id = card.scryfallId || card.id;
                if (id) onQuantityDelta(id, -1);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              −
            </button>
          )}
          <span className="table-cell__quantity-value">
            {card.quantity || 1}
          </span>
          {canEditQuantity && (
            <button
              type="button"
              className="table-qty-btn"
              onClick={(e) => {
                e.stopPropagation();
                const id = card.scryfallId || card.id;
                if (id) onQuantityDelta(id, +1);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              +
            </button>
          )}
        </div>
      </td>

      {/* Price */}
      <td className="data-table__cell--num">
        {formatPrice(card.price)}
      </td>

      {/* Owned Quantity */}
      <td className="data-table__cell--num">
        {ownedQty}
      </td>

      {/* Actions */}
      {(isOwnProfile || onShowDetails) && (
        <td className="data-table__cell--num">
          {/* Die Zelle steht, solange die Spalte steht — sonst verrutschen die Spalten
              einer Zeile. Ohne oracleId bleibt sie im fremden Deck leer: „Details" ist
              dort der einzige mögliche Eintrag und braucht die Kennung. */}
          {(isOwnProfile || card.oracleId) && (
            <CardContextMenu
              card={card}
              onShowDetails={onShowDetails && card.oracleId ? onShowDetails : undefined}
              onSwitchPrint={onSwitchPrint && card.oracleId ? onSwitchPrint : undefined}
              onSetCommander={isOwnProfile ? handleSetCommander : undefined}
              deckLabels={deckLabels}
              cardLabels={cardLabels}
              onAssignLabel={(label) => onAssignLabel(card.deckCardEntryId, label)}
              onRemoveLabel={(labelId) => onRemoveLabel(card.deckCardEntryId, labelId)}
              onNewLabel={isOwnProfile ? () => onNewLabelRequest(card.deckCardEntryId) : undefined}
            />
          )}
        </td>
      )}
    </tr>
  );
}

// ========== KATEGORIE-TABELLE ==========
function CategoryTable({
  category,
  categoryId,
  excluded,
  cards,
  isDropTarget,
  externalDragging = false,
  externalDropProps,
  canEditQuantity,
  onQuantityDelta,
  isOwnProfile,
  onSetAsCommander,
  onRemoveCategory,
  canRemove,
  onShowDetails,
  onSwitchPrint,
  onPreviewStart,
  onPreviewEnd,
  isDraggingCategory,
  isDroppable,
  hoverDelayMs,
  activePreviewCardId,
  stickyTop = 0,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  strategy,
  renameCategory,
  onCategoryRenamed,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Cards sind vom Parent bereits gefiltert
  const visibleCards = cards;
  // Muss mit dem Gate der Aktions-Zelle in TableRow übereinstimmen — <col>, <th>,
  // Zelle und der colSpan des Drop-Placeholders zählen dieselbe Spalte.
  const showActionColumn = isOwnProfile || typeof onShowDetails === 'function';
  
  const totalCards = useMemo(() =>
    visibleCards.reduce((sum, c) => sum + (c.quantity || 1), 0),
    [visibleCards]
  );
  const totalPrice = useMemo(
    () => visibleCards.reduce((sum, c) => sum + ((Number(c.price) || 0) * (Number(c.quantity) || 0)), 0),
    [visibleCards]
  );
  
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `category-${category}`,
    data: { category, categoryId },
    disabled: !isDroppable,
  });
  
  const { setNodeRef: setSortRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: `cat-${category}`,
  });
  
  const setCombinedRef = (el) => {
    setDropRef(el);
    setSortRef(el);
  };
  
  // Nur als Drop-Target hervorheben wenn eine Karte gedraggt wird, nicht wenn eine Kategorie gedraggt wird
  const showAsDropTarget = !isDraggingCategory && (isDropTarget || isOver);
  const styleTransform = transform
    ? CSS.Transform.toString({
      ...transform,
      scaleX: 1,
      scaleY: 1,
    })
    : undefined;

  // Rename nur für echte, persistierte Kategorien des eigenen Decks
  // (nicht 'other'/Nicht-Kategorie-Gruppierungen, nicht Commander — der hat keinen Header hier).
  const canRenameCategory = Boolean(isOwnProfile && strategy?.persistent && categoryId != null);

  const handleRenameCategory = useCallback(async (nextName) => {
    const result = await renameCategory?.(categoryId, nextName);
    onCategoryRenamed?.(category, result);
  }, [categoryId, category, renameCategory, onCategoryRenamed]);

  return (
    <div
      ref={setCombinedRef}
      {...attributes}
      data-drop-group={category}
      data-external-target={isDropTarget && externalDragging ? 'true' : undefined}
      {...externalDropProps}
      className={`category-table ${showAsDropTarget ? 'is-drop-target' : ''}${externalDragging && !showAsDropTarget ? ' is-external-hint' : ''}`}
      style={{
        transform: styleTransform,
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {/* Header - komplett dragbar für Kategorie-Sortierung */}
      <CategoryHeader
        name={category}
        count={totalCards}
        price={formatPrice(totalPrice)}
        excluded={excluded}
        dragHandle={isOwnProfile}
        dragListeners={isOwnProfile ? listeners : undefined}
        isDropTarget={showAsDropTarget}
        stickyTop={stickyTop}
        collapsible
        collapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed((v) => !v)}
        canRename={canRenameCategory}
        onRename={(next) => handleRenameCategory(next)}
        canRemove={canRemove && isOwnProfile}
        onRemove={() => onRemoveCategory?.(category)}
      />

      {/* Table — immer rendern wenn expanded, auch bei 0 sichtbaren Karten.
         Spalten-Header bleiben als Affordance sichtbar, Placeholder-<tr>
         (siehe Drop-Animation) braucht den <tbody>-Anker. */}
      {!isCollapsed && (
        <div className="de-table-sheet">
          <table className="data-table de-table">
            <colgroup>
              {/* Summe = 100% mit und ohne Aktionen-Spalte: Mit Actions: 20+4+9+10+12+5+11+7+8+8+6=100%; Ohne Actions: 26+4+9+10+12+5+11+7+8+8=100% */}
              <col style={{ width: showActionColumn ? '20%' : '26%' }} />{/* Name */}
              <col style={{ width: '4%' }} />{/* Labels */}
              <col style={{ width: '9%' }} />{/* Supertypen */}
              <col style={{ width: '10%' }} />{/* Typen */}
              <col style={{ width: '12%' }} />{/* Subtypen */}
              <col style={{ width: '5%' }} />{/* CMC */}
              <col style={{ width: '11%' }} />{/* Manakosten */}
              <col style={{ width: '7%' }} />{/* Anzahl */}
              <col style={{ width: '8%' }} />{/* Preis */}
              <col style={{ width: '8%' }} />{/* Im Besitz */}
              {showActionColumn && <col style={{ width: '6%' }} />}{/* Aktionen */}
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th></th>
                <th>Supertypen</th>
                <th>Typen</th>
                <th>Subtypen</th>
                <th className="data-table__cell--num">CMC</th>
                <th className="data-table__cell--num">Manakosten</th>
                <th className="data-table__cell--num">Anzahl</th>
                <th className="data-table__cell--num">Preis</th>
                <th className="data-table__cell--num">Im Besitz</th>
                {showActionColumn && <th></th>}
              </tr>
            </thead>
            <tbody>
              {visibleCards.map((card, idx) => (
                <TableRow
                  key={`${card.id}-${idx}`}
                  card={card}
                  canEditQuantity={canEditQuantity}
                  onQuantityDelta={onQuantityDelta}
                  isOwnProfile={isOwnProfile}
                  onSetAsCommander={onSetAsCommander}
                  onShowDetails={onShowDetails}
                  onSwitchPrint={onSwitchPrint}
                  onPreviewStart={onPreviewStart}
                  onPreviewEnd={onPreviewEnd}
                  hoverDelayMs={hoverDelayMs}
                  activePreviewCardId={activePreviewCardId}
                  highlightedNewCardId={highlightedNewCardId}
                  deckLabels={deckLabels}
                  cardLabels={getCardLabels(card.deckCardEntryId)}
                  onAssignLabel={onAssignLabel}
                  onRemoveLabel={onRemoveLabel}
                  onNewLabelRequest={onNewLabelRequest}
                />
              ))}
              {/* Drop-Animations-Placeholder: 4-px-Zeile am Tabellen-Ende als
                  Mess-Anker. colSpan matched die Spalten aus <thead> (10 base,
                  +1 Actions-Spalte falls sie steht). */}
              <tr
                className="card-drop-placeholder card-drop-placeholder--table"
                data-placeholder-category={category}
                style={{ height: 4 }}
              >
                <td colSpan={showActionColumn ? 11 : 10} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ========== HAUPTKOMPONENTE ==========
export default function TableView({
  externalDrop,
  deck,
  isOwnProfile,
  onCategoryChange,
  canEditQuantity = false,
  onQuantityDelta,
  sortBy = 'name',
  searchTerm = '',
  highlightedCmc = null,
  strategy,
  matchingCardIds = null, // Set von Card-IDs die bei Syntax-Suche matchen (null = keine Syntax-Suche)
  isDraggingCategory = false,
  onRegisterDndHandlers,
  showExcluded = true,
  onRemoveCardsByCategory,
  onShowDetails,
  onSwitchPrint,
  stickyTop = 0,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  onDropError,
  onCategoryRenamed,
  onCardMoved,
  onCatalogCardDrop,
}) {
  const { authToken } = useAuth();

  // Hook für Deck-Karten-Aktionen (setAsCommander)
  const { setAsCommander } = useDeckCardActions({
    deck,
    canEdit: isOwnProfile,
    onError: onDropError,
  });

  const [previewCard, setPreviewCard] = useState(null);
  const [previewPosition, setPreviewPosition] = useState(null);
  
  // Preview handlers
  const handlePreviewStart = useCallback((card, position) => {
    setPreviewCard(card);
    setPreviewPosition(position);
  }, []);
  
  const handlePreviewEnd = useCallback(() => {
    setPreviewCard(null);
    setPreviewPosition(null);
  }, []);
  
  // Gemeinsame Hooks für View-State, Transparenz und DnD
  const { groups, categories, moveCategory, removeCategory, addCategory, renameCategory, excludedCategoryNames } = useDeckViewState({
    deck, sortBy, strategy, showExcluded, isOwnProfile, authToken,
  });

  const shouldBeTransparent = useShouldBeTransparent({ highlightedCmc, searchTerm, matchingCardIds });
  const isFilterActive = !!(searchTerm?.trim() || highlightedCmc !== null || matchingCardIds !== null);

  const { dropTarget, handleRemoveCategory, handleSetAsCommander, pendingDeleteCategory, confirmDeleteCategory, cancelDeleteCategory, applyManualOrder } = useDeckDragHandlers({
    deck, isOwnProfile, onCategoryChange, categories,
    moveCategory, removeCategory, addCategory, sortBy, setAsCommander,
    onRemoveCardsByCategory, onRegisterDndHandlers, cardWidth: null,
    onDragStartExtra: handlePreviewEnd,
    strategy, excludedCategoryNames, onDropError, onCardMoved, onCatalogCardDrop,
  });

  const groupsFiltered = useMemo(() => {
    return groups.map(g => {
      const ordered = applyManualOrder(g.name, g.cards);
      const cards = isFilterActive ? ordered.filter(card => !shouldBeTransparent(card)) : ordered;
      return { ...g, cards };
    });
  }, [groups, applyManualOrder, isFilterActive, shouldBeTransparent]);
  
  if (!deck) {
    return <div className="table-view__empty">Kein Deck geladen</div>;
  }
  
  return (<>
      <div className="table-view">
        <SortableContext items={groupsFiltered.map(g => `cat-${g.name}`)} strategy={rectSortingStrategy}>
          {groupsFiltered.map((group) => {
            const externesZiel = externalDrop && group.id != null && !group.isCommander;
            return (
            <CategoryTable
              key={group.name}
              category={group.name}
              categoryId={group.id}
              excluded={!!(group.excluded || group.type === 'excluded')}
              cards={group.cards}
              isDropTarget={dropTarget === group.name || (externesZiel && externalDrop.target === group.name)}
              externalDragging={Boolean(externesZiel && externalDrop.active)}
              externalDropProps={externesZiel ? externalDrop.propsFor(group.name, group.id) : undefined}
              canEditQuantity={canEditQuantity}
              onQuantityDelta={onQuantityDelta}
              isOwnProfile={isOwnProfile}
              onSetAsCommander={handleSetAsCommander}
              onShowDetails={onShowDetails}
              onSwitchPrint={onSwitchPrint}
              onRemoveCategory={handleRemoveCategory}
              canRemove={strategy.editable}
              isDroppable={strategy.editable || !!group.excluded || group.type === 'excluded'}
              onPreviewStart={handlePreviewStart}
              onPreviewEnd={handlePreviewEnd}
              isDraggingCategory={isDraggingCategory}
              hoverDelayMs={HOVER_DELAY_MS}
              activePreviewCardId={previewCard?.id ?? null}
              stickyTop={stickyTop}
              highlightedNewCardId={highlightedNewCardId}
              deckLabels={deckLabels}
              getCardLabels={getCardLabels}
              onAssignLabel={onAssignLabel}
              onRemoveLabel={onRemoveLabel}
              onNewLabelRequest={onNewLabelRequest}
              strategy={strategy}
              renameCategory={renameCategory}
              onCategoryRenamed={onCategoryRenamed}
            />
            );
          })}
        </SortableContext>

        {/* Card Preview Tooltip */}
        <CardPreview card={previewCard} position={previewPosition} />
      </div>
      <ConfirmDialog
        open={!!pendingDeleteCategory}
        onOpenChange={(next) => { if (!next) cancelDeleteCategory(); }}
        onConfirm={confirmDeleteCategory}
        title="Kategorie löschen"
        description="Wenn du diese Kategorie löschst, werden alle enthaltenen Karten aus dem Deck entfernt."
        confirmLabel="Löschen"
        tone="danger"
      />
  </>);
}
